import type { Currencies } from "../settingsStore";
import type {
    CreatePriceHistoryEntryParams,
    IPriceHistoryEntry,
    PriceHistoryRepository,
    UpdatePriceHistoryEntryParams,
} from ".";

const createFixture = (overrides?: Partial<IPriceHistoryEntry>): IPriceHistoryEntry => ({
    id: Math.random() * 1000,
    channelId: 1721,
    price: 1199,
    titleId: 222553,
    currency: "EUR",
    endDate: new Date("2026-05-20").toISOString(),
    startDate: new Date("2026-01-20").toISOString(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    childCurrencies: [],
    ...overrides,
});
export const priceHistoryFixtures: IPriceHistoryEntry[] = [
    createFixture({ childCurrencies: [{ currency: "CHF", country: "AI", price: 1300 }] }),
    createFixture({ startDate: new Date("2023-05-21").toISOString(), endDate: new Date("2023-12-24").toISOString() }),
    createFixture({ startDate: new Date("2026-05-21").toISOString(), endDate: null }),
];

const STORAGE_KEY = "priceHistoryInMem";
const CREATE_TITLE_ID = "CREATE_TITLE_ID";

export class PriceHistoryInMem implements PriceHistoryRepository {
    priceHistoryData: Record<number, IPriceHistoryEntry[]> = {};

    constructor(initialData: Record<number, IPriceHistoryEntry[]> = {}) {
        const stored = localStorage.getItem(STORAGE_KEY);
        this.priceHistoryData = stored ? JSON.parse(stored) : initialData;
    }

    private persist() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.priceHistoryData));
    }

    async fetchPriceHistory(titleId: number): Promise<IPriceHistoryEntry[]> {
        return this.priceHistoryData[titleId] ?? [];
    }

    async createPriceHistoryEntry(params: CreatePriceHistoryEntryParams): Promise<IPriceHistoryEntry> {
        const newEntry: IPriceHistoryEntry = {
            ...params,
            id: Date.now(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            currency: params.currency as Currencies,
        };
        const existingEntries = this.priceHistoryData[params.titleId ?? CREATE_TITLE_ID] ?? [];
        this.priceHistoryData[params.titleId ?? CREATE_TITLE_ID] = [...existingEntries, newEntry];
        this.persist();
        return newEntry;
    }

    async updatePriceHistoryEntry(params: UpdatePriceHistoryEntryParams): Promise<IPriceHistoryEntry> {
        const entries = this.priceHistoryData[params.titleId ?? CREATE_TITLE_ID] ?? [];
        const index = entries.findIndex((entry) => entry.id === params.id);
        if (index === -1) {
            throw new Error("Entry not found");
        }
        const updatedEntry: IPriceHistoryEntry = {
            ...entries[index],
            ...params,
            updatedAt: new Date().toISOString(),
            currency: params.currency as Currencies,
        };
        this.priceHistoryData[params.titleId ?? CREATE_TITLE_ID] = entries.map((e, i) =>
            i === index ? updatedEntry : e,
        );
        this.persist();
        return updatedEntry;
    }

    async deletePriceHistoryEntry(titleId: number, entryId: number): Promise<void> {
        const entries = this.priceHistoryData[titleId] ?? [];
        const index = entries.findIndex((entry) => entry.id === entryId);
        if (index === -1) {
            throw new Error("Entry not found");
        }
        this.priceHistoryData[titleId] = entries.filter((_, i) => i !== index);
        this.persist();
    }

    setPriceHistory(titleId: number, entries: IPriceHistoryEntry[]) {
        this.priceHistoryData[titleId] = entries;
        this.persist();
    }

    reset() {
        this.priceHistoryData = {};
        localStorage.removeItem(STORAGE_KEY);
    }
}
